/**
 * Select-slicer value semantics shared by the viewer runtime, the picker UI and the
 * block-facing `slicerClause` helper.
 *
 * Why this exists: a multi-select value is a plain `string[]`, and until now "everything
 * selected" was indistinguishable from "this exact list", and a NULL/blank cell could never be
 * selected at all. Blocks hand-rolled `col IN (?,?,?)`, which silently drops NULL rows, drops
 * values added after the list was saved, and is re-implemented slightly differently in every
 * block. The fixes here are additive: the value blocks receive is still a `string[]` (existing
 * `.map(() => '?')` code keeps working), "all" rides on it as a non-enumerable marker set at
 * hand-off, and blank is a reserved option value.
 *
 * Pure and dependency-free on purpose: jest runs these in node, and the sandbox port in
 * `web_dashboard_js_runtime_sandbox.py` (`slicerClause`, `__withAllMarker__`) mirrors this
 * file line for line — change both together.
 */

export interface SelectOption {
  value: string;
  label: string;
}

/** Reserved option value meaning "the cell is NULL or empty". Never a real data value. */
export const BLANK_OPTION_VALUE = "__blank__";
export const BLANK_OPTION_LABEL = "(Blank)";

const ALL_MARKER = "__all";

export interface SqlClause {
  /** A boolean SQL expression, safe to append after `WHERE 1=1 AND`. */
  sql: string;
  bindings: unknown[];
}

const isBlankish = (value: unknown): boolean =>
  value === null || value === undefined || (typeof value === "string" && value.trim() === "");

/**
 * Coerce whatever a `get_options_js_function` returned into the option list the UI renders:
 * NULL / empty values collapse into one "(Blank)" option, null labels are filled in, and
 * duplicate values are dropped (first wins). Non-string values are left as they are so a
 * numeric column keeps matching under SQLite affinity.
 */
export const normalizeSelectOptions = (options: unknown): SelectOption[] => {
  if (!Array.isArray(options)) {
    return [];
  }
  const out: SelectOption[] = [];
  const seen = new Set<unknown>();
  for (const raw of options) {
    let value: unknown;
    let label: unknown;
    if (raw !== null && typeof raw === "object") {
      value = (raw as { value?: unknown }).value;
      label = (raw as { label?: unknown }).label;
    } else {
      value = raw;
      label = raw;
    }
    if (isBlankish(value)) {
      value = BLANK_OPTION_VALUE;
      label = BLANK_OPTION_LABEL;
    } else if (isBlankish(label)) {
      label = String(value);
    }
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    out.push({ value: value as string, label: String(label) });
  }
  return out;
};

/**
 * Normalise a selected-values list the way `normalizeSelectOptions` normalises options: NULL /
 * empty entries become the blank option value and duplicates are dropped. A legacy default
 * function (`SELECT DISTINCT col` over a column with NULLs) returns `[..., null]`; without this
 * it no longer matches the normalised options and an "everything" default reads as a subset.
 */
export const normalizeSelectValues = (values: unknown): string[] => {
  if (!Array.isArray(values)) {
    return [];
  }
  const out: string[] = [];
  const seen = new Set<unknown>();
  for (const raw of values) {
    const value = isBlankish(raw) ? BLANK_OPTION_VALUE : raw;
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    out.push(value as string);
  }
  return out;
};

/** True when every option is selected (set semantics: order and duplicates do not matter). */
export const isAllSelected = (
  values: readonly unknown[],
  options: readonly SelectOption[]
): boolean => {
  if (!Array.isArray(values) || options.length === 0) {
    return false;
  }
  const selected = new Set<unknown>(values);
  return options.every(o => selected.has(o.value));
};

/**
 * A copy of `values` carrying the non-enumerable "all" marker when `all` is set. The marker
 * survives being passed to render functions but is invisible to JSON.stringify, spreads and
 * `.map`, so nothing that stores or compares the value can see it.
 */
export const withAllMarker = (values: string[], all: boolean): string[] => {
  const copy = values.slice();
  if (all) {
    Object.defineProperty(copy, ALL_MARKER, { value: true, enumerable: false });
  }
  return copy;
};

export const hasAllMarker = (value: unknown): boolean =>
  Array.isArray(value) && (value as unknown as Record<string, unknown>)[ALL_MARKER] === true;

/**
 * Mark every multi-select value in `state` whose selection covers all of its resolved options.
 * Applied once at hand-off to a render function, from the options resolved for THIS load, so
 * a list saved when the data had five regions still reads as "all" after a sixth appears —
 * as long as the user has not narrowed it.
 */
export const markAllSelections = <T extends Record<string, unknown>>(
  state: T,
  resolvedOptions: Record<string, SelectOption[]>
): T => {
  const out: Record<string, unknown> = { ...state };
  for (const [name, value] of Object.entries(state)) {
    const options = resolvedOptions[name];
    if (Array.isArray(value) && options && options.length > 0) {
      out[name] = withAllMarker(value as string[], isAllSelected(value, options));
    }
  }
  return out as T;
};

const blankClause = (column: string): string => `(${column} IS NULL OR TRIM(${column}) = '')`;

/**
 * The SQL predicate for a select-slicer value against `column`, with its bindings. Blocks use
 * it as `WHERE 1=1 AND ${clause.sql}` and spread `clause.bindings` into `runSQL`.
 *
 * - everything selected (or no value yet) → `1=1`: no filter, so NULL rows stay in
 * - nothing selected → `1=0`: the user chose "None"
 * - a subset → `col IN (?, …)`, plus `col IS NULL OR TRIM(col) = ''` when "(Blank)" is in it
 * - a single-select string → `col = ?`, "" → `1=1`, blank → the NULL test
 *
 * `column` is interpolated verbatim: pass an identifier or expression, never user input.
 */
export const slicerClause = (value: unknown, column: string): SqlClause => {
  if (typeof column !== "string" || column.trim() === "") {
    throw new Error("slicerClause(value, column): column is required");
  }
  const none: SqlClause = { sql: "1=1", bindings: [] };
  if (value === null || value === undefined) {
    return none;
  }
  if (typeof value === "string") {
    if (value === "") {
      return none;
    }
    if (value === BLANK_OPTION_VALUE) {
      return { sql: blankClause(column), bindings: [] };
    }
    return { sql: `${column} = ?`, bindings: [value] };
  }
  if (Array.isArray(value)) {
    if (hasAllMarker(value)) {
      return none;
    }
    if (value.length === 0) {
      return { sql: "1=0", bindings: [] };
    }
    const hasBlank = value.includes(BLANK_OPTION_VALUE);
    const rest = value.filter(v => v !== BLANK_OPTION_VALUE);
    const parts: string[] = [];
    if (rest.length > 0) {
      parts.push(`${column} IN (${rest.map(() => "?").join(", ")})`);
    }
    if (hasBlank) {
      parts.push(blankClause(column));
    }
    return { sql: parts.length === 1 ? parts[0] : `(${parts.join(" OR ")})`, bindings: rest };
  }
  // A date range or an unknown shape is not a membership filter.
  return none;
};
